import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { Brand, Category, CustomField, License, Office, Agent, Warehouse } from "types";
import { UIInputFF, UIReactSelectFF } from "components/Adapter";
import { Package, Settings } from 'lucide-react';
import { Media } from "types";
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";
// NOTE: This is a **UI-only redesign** of your existing SignageAddRotationalProduct
// The business logic (APIs, payloads, state names, and submit body) remains intact.
// It adopts the same visual structure/patterns used in SignageSimpleProduct (tabs,
// card-like sections, mobile-first responsive behavior, subtle borders, etc.).

// ------------ Local Types kept from your current file ------------
type Attribute = {
  id: string;
  name: string;
  values: string[];
  description: string;
  status: string;
};

interface Installer {
  supplier_id: string;
  name: string;
  email: string;
}

type TaxGroup = {
  id: string;
  name: string;
  tax_rules: TaxRule[];
  status: string;
};

type TaxRule = {
  _id: string;
  country: string;
  state: string;
  stateCode: string;
  county: string;
  countyCode: string;
  zipCodes: string[];
  taxRate: number;
  comments: string;
  status: string;
};

const INPUT_BG = "#f5f6f8";
const BORDER = "#e5e7eb";

const SignageAddRotationalProduct: React.FC = () => {
  // --------- State preserved from your original component ---------
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // State for parent signage firm (for SignageTeammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);

  // signageId: parent signage firm when SignageTeammember, else userId
  const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : (userId || "");

  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [taxGroups, setTaxGroups] = useState<TaxGroup[]>([]);
  const [taxRules, setTaxRules] = useState<TaxRule[]>([]);

  // Image uploads kept as in your original file
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[] | undefined>(undefined);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [options, setOptions] = useState<{ attribute: string; value: string }[]>([]);

  // NEW: tabs similar to SignageSimpleProduct
  const [activeTab, setActiveTab] = useState<"details" | "images">("details");

  // Warehouse list (UI) — preserves your original structure/logic
  const [warehouseDetails, setWarehouseDetails] = useState<
    { warehouseId: string; label: string; availableQty: number }[]
  >([]);

  // Pagination preserved (logic unchanged)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(warehouseDetails.length / itemsPerPage) || 1;
  const paginatedData = useMemo(
    () => warehouseDetails.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [warehouseDetails, currentPage]
  );
  const handlePageChange = (page: number) => setCurrentPage(page);

  // Fetch parent hierarchy for SignageTeammember login
  useEffect(() => {
    const loadParentHierarchy = async () => {
      if (isSignageTeammember && token) {
        const hierarchyData = await fetchParentHierarchy(token);
        if (hierarchyData) {
          setParentSignageId(hierarchyData.parentSignageId);
        }
      }
    };
    loadParentHierarchy();
  }, [isSignageTeammember, token]);

  // ---------- Effects preserved (API calls & success/error handling) ----------
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/brands?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setBrands(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, [token]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/categories?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [token]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/warehouses?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setWarehouses(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchWarehouses();
  }, [token]);

  useEffect(() => {
    setWarehouseDetails(
      (warehouses || []).map((w) => ({ warehouseId: (w as any)._id, label: (w as any).label, availableQty: 0 }))
    );
  }, [warehouses]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/product-attributes`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setAttributes(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchAttributes();
  }, [token]);

  useEffect(() => {
    const fetchInstallers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/agent/suppliers?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch installers data");
        const data = await response.json();
        if (Array.isArray(data)) setInstallers(data);
      } catch (err) {
        // keep UI silent — non-blocking section
        console.error("Error fetching installers data:", err);
      }
    };
    fetchInstallers();
  }, [token]);


  async function uploadProductImagesToCloudDrive(): Promise<string[]> {
    const tok = (token || "").trim() || null;
    // use the files you already collected in previewFiles
    const files = (previewFiles || []).map(p => p.file);
    if (!files.length) return [];

    const ids = await uploadFilesAndGetIds(files, tok);
    setPreviewFiles([]);
    return ids;
  }


  // ---------- Formik (preserved initialValues + validation + onSubmit payload) ----------
  const formik = useFormik({
    initialValues: {
      name: "",
      sku: "",
      description: "",
      brand: "",
      category: "",
      item_condition: "",
      condition_note: "",
      purchase_price: "",
      retail_price: "",
      length: "",
      height: "",
      width: "",
      weight: "",
      threshold_qty: "",
      attribute: "",
      parameter: "",
      notes: "",
      images: null as any,
      statuss: "Active",
      warehouse: "",
      allocated_qty: 0,
      available_qty: 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().min(2, "Name must be at least 2 characters long").max(250, "Name cannot be longer than 50 characters").required("Name is required"),
      sku: Yup.string().required("SKU is required"),
      retail_price: Yup.number().typeError("Enter a valid number").required("Retail price is required"),
      // statuss: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);
      try {
        const imageIds = await uploadProductImagesToCloudDrive();

        const altTexts: string[] = (previewFiles ?? []).map(p => (p as any).alt_text ?? "");

        // Option A: first image is default, others false
        const images: Media[] = imageIds.map((id, idx) => ({
          url: id,
          alt_text: altTexts[idx] || "",
          default: idx === 0,   // first one default = true; change to false if you don't want any default
        }));

        try {
          if (pendingDeleteIds.length > 0) {
            await hardDeletePresignedFiles(pendingDeleteIds, token || null);
            setPendingDeleteIds([]); // clear
          }
        } catch (delErr: any) {
          console.error("Image hard delete (batch) failed:", delErr);
          // Don't block success — just inform
          showError(delErr?.message || "Some removed images could not be deleted.");
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            rotational_product: {
              name: values.name,
              sku: values.sku,
              brand: values.brand,
              category: values.category,
              description: values.description,
              images: images,
              item_condition: values.item_condition,
              purchase_price: Number(values.purchase_price),
              retail_price: Number(values.retail_price),
              package_dimensions: {
                length: Number(values.length) || 0,
                width: Number(values.width) || 0,
                height: Number(values.height) || 0,
                weight: Number(values.weight) || 0,
                weight_type: "kg",
              },
              variants: [],
              is_rotational: true,
              linked_mode: "none",
              locked_status: "none",
              product_location_status: "none",
              current_status: "none",
              status: values.statuss,
            },
            inventory_type: "rotational",
            signage_company_id: signageId,
            warehouse_details: warehouseDetails.map((item) => ({
              warehouse: warehouses.find((w) => (w as any)._id === item.warehouseId),
              location: {
                warehouse_id: item.warehouseId,
                warehouse: item.label,
                zone_id: null,
                zone: null,
                rack_id: null,
                rack: null,
                bin_id: null,
                bin: null,
              },
              available: item.availableQty,
              allocated: 0,
              remaining: item.availableQty,
            })),
            serialized_units: [],
            total_quantity: warehouseDetails.reduce((acc, it) => acc + Number(it.availableQty || 0), 0),
            total_allocated_quantity: 0,
            serialized_units_count: 0,
            available_for_serialization: 0,
          }),
        });

        const result = await response.json();
        if (!response.ok) {
          showError(result?.error || "Failed to add rotational product");
          return;
        }
        showSuccess("Rotational product added successfully!");
        navigate(`${baseRoute}/rotationalProducts`);
      } catch (error) {
        console.error("Fetch Error:", error);
        showError("Failed to add rotational product.");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  function handleWarehouseFieldChange(warehouseId: string, value: number) {
    setWarehouseDetails((prev) => prev.map((it) => (it.warehouseId === warehouseId ? { ...it, availableQty: Math.max(0, Number(value) || 0) } : it)));
  }
 
  const removeUploadedImages = (ids: string[]) => {
    const clean = (ids || []).map(String).filter(Boolean);
    if (!clean.length) return;

    // mark for delete on SAVE
    setPendingDeleteIds(prev => Array.from(new Set([...(prev || []), ...clean])));

    // remove from current UI list
    setUploadedFileMeta(prev => {
      const next = (prev || []).filter((m: any) => !clean.includes(String(m?.url)));
      // ensure exactly one default if any images remain
      if (next.length && !next.some((m: any) => m?.default)) {
        next[0] = { ...next[0], default: true };
      }
      return next;
    });
  };

  /** Convenience: remove a single existing image by its file id (stored in `url`). */
  const removeUploadedImage = (id: string) => removeUploadedImages([id]);

  /** Remove a local (not uploaded yet) preview by its index. */
  const removePreviewImage = (index: number) => {
    setPreviewFiles(prev => {
      const arr = [...prev];
      const target = arr[index];
      if (target?.url) URL.revokeObjectURL(target.url);
      arr.splice(index, 1);
      return arr;
    });
  };
  // ---------- UI ----------
  return (
    <div className="container-xxl py-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Add Rotational Product</h4>
        {/* <div>
          <button type="button" className="btn btn-outline-secondary btn-sm me-2" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left" /> Back
          </button>
          <button type="submit" className="btn btn-primary btn-sm" onClick={() => formik.handleSubmit()} disabled={formSubmitting}>
            {formSubmitting ? "Saving..." : "Save Product"}
          </button>
        </div> */}
      </div>

      {/* Tabs (same style as SimpleProduct) */}
      <div className="rc-tabs mb-4">
        <button type="button" className={`rc-tab ${activeTab === "details" ? "rc-tab-active" : ""}`} onClick={() => setActiveTab("details")}>
          <i className="bi bi-box-seam me-2" /> Product Details
        </button>
        <button type="button" className={`rc-tab ${activeTab === "images" ? "rc-tab-active" : ""}`} onClick={() => setActiveTab("images")}>
          <i className="bi bi-image me-2" /> Images
        </button>
      </div>

      {/* ============== DETAILS TAB ============== */}
      {activeTab === "details" && (
        <form onSubmit={formik.handleSubmit} className="box-shadow-datatables shadow-border-1 p-5">
          <div className="row g-4">
            {/* LEFT */}
            <div className="col-lg-6">
              <div className="mb-3 fw-semibold text-uppercase small text-secondary">
                <div {...iconLabelProps(16)}>
                  <Package {...withIconStyle("#4a5565", 19)} />

                  <span className="header-color-product"> Product Information</span>
                </div>
              </div>

              <div className="mb-3">
                <UIInputFF formik={formik as any} name="name" label="Product Name" required placeholder="Enter product name" />
              </div>

              <div className="mb-3 col-md-8">
                <UIReactSelectFF
                  formik={formik as any}
                  name="brand"
                  label="Brand"
                  placeholder="Select brand"
                  options={(Array.isArray(brands) ? brands : []).map((b) => ({ value: (b as any).name, label: (b as any).name }))}
                />
              </div>

              <div className="mb-3 col-md-8">
                <UIReactSelectFF
                  formik={formik as any}
                  name="category"
                  label="Category"
                  placeholder="Select category"
                  options={(Array.isArray(categories) ? categories : []).map((c) => ({ value: (c as any).name, label: (c as any).name }))}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Product Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  rows={4}
                  value={(formik.values as any).description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ borderRadius: 10, background: INPUT_BG, borderColor: BORDER, fontSize: 14 }}
                  placeholder="Enter product description..."
                />
              </div>

              <div className="mb-3">
                <div className="row g-2">
                  <div className="col-4">
                    <UIInputFF formik={formik as any} name="threshold_qty" label="Threshold Quantity" type="number" placeholder="0" />
                  </div>
                  <div className="col-4">
                    <UIReactSelectFF
                      formik={formik as any}
                      name="statuss"
                      label="Status"
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      placeholder="Select"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-lg-6">
              <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                <i className="bi bi-currency-dollar pe-1" />
                SKU & Pricing
              </div>

              <div className="mb-3">
                <UIInputFF formik={formik as any} name="sku" label="SKU" required placeholder="Enter SKU" />
              </div>

              <div className="mb-3">
                <UIInputFF formik={formik as any} name="purchase_price" label="Purchase Price" placeholder="0.00" type="number" />
              </div>

              <div className="mb-4">
                <UIInputFF formik={formik as any} name="retail_price" label="Retail Price" required placeholder="0.00" type="number" />
              </div>

              <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                <i className="bi bi-paperclip pe-1" />
                Physical Properties
              </div>

              <div className="border rounded-3 p-3 mb-3">
                <div className="mb-2 small text-muted">Dimensions/Measurements (Optional)</div>
                <div className="row g-2">
                  <div className="col-4"><UIInputFF formik={formik as any} name="length" type="number" placeholder="Length" /></div>
                  <div className="col-4"><UIInputFF formik={formik as any} name="height" type="number" placeholder="Height" /></div>
                  <div className="col-4"><UIInputFF formik={formik as any} name="width" type="number" placeholder="Width" /></div>
                  <div className="col-6"><UIInputFF formik={formik as any} name="weight" type="number" placeholder="Weight (kg)" /></div>
                </div>
              </div>

              <div className="mb-3">
                <UIReactSelectFF
                  formik={formik as any}
                  name="item_condition"
                  label="Item Condition"
                  placeholder="Brand New"
                  options={[
                    { value: "new", label: "New" },
                    { value: "used", label: "Used" },
                    { value: "damaged", label: "Damaged" },
                  ]}
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Condition Notes</label>
                <textarea
                  id="condition_note"
                  name="condition_note"
                  className="form-control"
                  rows={4}
                  value={(formik.values as any).condition_note}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ borderRadius: 10, background: INPUT_BG, borderColor: BORDER, fontSize: 14 }}
                  placeholder="Enter Item Condition (optional)"
                />
              </div>
            </div>
          </div>

          {/* ====== WAREHOUSE (redesigned, same logic) ====== */}
          <hr className="my-4" />
          <section className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Warehouse Quantities</h6>
              <small className="text-muted">Scroll to see more • {warehouseDetails.length} warehouses</small>
            </div>

            {/* Desktop style: one compact card per row */}
            <div className="rdt-desktop">
              <div className="production-scroll" style={{ maxHeight: 360, overflowY: "auto", paddingRight: 6 } as React.CSSProperties}>
                {paginatedData.map((item) => (
                  <div key={item.warehouseId} className="rounded-4 border p-3 mt-2 bg-white shadow-sm">
                    <div className="row g-3 align-items-center">
                      <div className="col-xl-6 col-md-6">
                        <input className="form-control" readOnly value={item.label} />
                      </div>
                      <div className="col-xl-6 col-md-6">
                        <div className="d-flex align-items-center justify-content-center gap-2 mx-auto" style={{ border: "2px solid #f2f2f2", borderRadius: 25, width: 140, height: 40 }}>
                          <button type="button" className="btn btn-link p-0" onClick={() => handleWarehouseFieldChange(item.warehouseId, Math.max(0, (item.availableQty || 0) - 1))} disabled={(item.availableQty || 0) <= 0}>
                            <i className="ri-subtract-line text-dark" />
                          </button>
                          <input
                            type="number"
                            min={0}
                            className="form-control text-center border-0"
                            style={{ width: 60 }}
                            value={item.availableQty}
                            onChange={(e) => handleWarehouseFieldChange(item.warehouseId, Number(e.target.value))}
                          />
                          <button type="button" className="btn btn-link p-0" onClick={() => handleWarehouseFieldChange(item.warehouseId, (item.availableQty || 0) + 1)}>
                            <i className="ri-add-line text-dark" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination controls (logic preserved) */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3 gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button key={i} type="button" className={`btn btn-sm ${currentPage === i + 1 ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => handlePageChange(i + 1)}>
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile style */}
            <div className="rdt-mobile">
              <div className="row g-3" style={{ maxHeight: 360, overflowY: "auto" }}>
                {paginatedData.map((item) => (
                  <div className="col-12" key={item.warehouseId}>
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="fw-semibold mb-2">{item.label}</div>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="text-muted">Available</span>
                          <div className="d-flex align-items-center gap-2" style={{ border: "2px solid #f2f2f2", borderRadius: 25, padding: "2px 8px" }}>
                            <button type="button" className="btn btn-link p-0" onClick={() => handleWarehouseFieldChange(item.warehouseId, Math.max(0, (item.availableQty || 0) - 1))} disabled={(item.availableQty || 0) <= 0}>
                              <i className="ri-subtract-line text-dark" />
                            </button>
                            <input
                              type="number"
                              min={0}
                              className="form-control text-center border-0"
                              style={{ width: 60 }}
                              value={item.availableQty}
                              onChange={(e) => handleWarehouseFieldChange(item.warehouseId, Number(e.target.value))}
                            />
                            <button type="button" className="btn btn-link p-0" onClick={() => handleWarehouseFieldChange(item.warehouseId, (item.availableQty || 0) + 1)}>
                              <i className="ri-add-line text-dark" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ====== ATTRIBUTES (same logic, prettier UI) ====== */}
          {/* <hr className="my-4" />
          <section className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Attributes</h6>
            </div>
            <div className="mt-2 mb-3 position-relative" style={{ zIndex: 10 }}>
              <div className="row g-2">
                <div className="col-md-3">
                  <UIReactSelectFF
                    formik={formik as any}
                    name="attribute"
                    label="Option"
                    placeholder="- Select -"
                    options={(attributes || []).map((a) => ({ value: a.name, label: a.name }))}
                  />
                </div>
                <div className="col-md-3">
                  <UIReactSelectFF
                    formik={formik as any}
                    name="parameter"
                    label="Value"
                    placeholder="- Select -"
                    options={(attributes.find((a) => a.name === (formik.values as any).attribute)?.values || []).map((v) => ({ value: v, label: v }))}
                  />
                </div>
                <div className="align-items-center col-md-6 d-flex justify-content-end mt-2 text-end">
                  <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={handleAddOption} disabled={!(formik.values as any).attribute || !(formik.values as any).parameter}>
                    <i className="bi bi-plus-lg me-1" /> Add Attribute
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive" style={{ maxHeight: 260, overflowY: "auto" }}>
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "45%" }}>Attribute name</th>
                    <th style={{ width: "45%" }}>Attribute value</th>
                    <th style={{ width: "10%" }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {options.map((opt, idx) => (
                    <tr key={`${opt.attribute}-${idx}`}>
                      <td>{opt.attribute}</td>
                      <td>{opt.value}</td>
                      <td className="text-center">
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveOption(idx)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section> */}

          <div className="d-flex justify-content-end mt-4">
            <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={formSubmitting}>
              {formSubmitting ? "Saving..." : "Save Product"}
            </button>
          </div>
        </form>
      )}

      {/* ============== IMAGES TAB ============== */}
      {activeTab === "images" && (
        <div className="box-shadow-datatables shadow-border-1 p-5">
          <div className="mb-3">
            <div
              className="imageuploadui d-flex flex-column align-items-center justify-content-center"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="bi bi-upload fs-2 mb-2" />
              <div>Click to upload product images</div>
              <small className="text-muted">PNG, JPG up to 5MB each</small>
            </div>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const newPreviews = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
                setPreviewFiles((prev) => [...prev, ...newPreviews]);
              }}
            />
          </div>

          {!!previewFiles.length && (
            <div className="row g-3">
              {previewFiles.map(({ file, url }, idx) => (
                <div className="col-sm-6 col-md-4 col-lg-3" key={`${file.name}-${idx}`}>
                  <div className="img-card">                           {/* fixed-height, relative */}
                    <button
                      type="button"
                      className="btn-close img-x"                      /* aligned top-right */
                      aria-label="Remove"
                      onClick={() => {
                        URL.revokeObjectURL(url);
                        setPreviewFiles((prev) => prev.filter((_, i) => i !== idx));
                         removePreviewImage(idx);
                        removeUploadedImage(url)
                      }}
                    />
                    <img src={url} alt={file.name} className="img-thumb" />
                  </div>
                  <div className="small text-center mt-1 text-muted text-truncate">
                    {file.name}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-primary save-btn surface-btn fw-bold fs-16" onClick={() => formik.handleSubmit()} disabled={formSubmitting}>
              {formSubmitting ? "Saving..." : "Save Product"}
            </button>
          </div>
        </div>
      )}

      {/* Inline styles (copied/adapted to match SimpleProduct aesthetics) */}
      <style>{`
        .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
        .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
        .rc-tab:hover{background:#eef2ff}
        .rc-tab.rc-tab-active{background:#6d28d9;border-color:#6d28d9;color:#fff;box-shadow:0 4px 14px rgba(109,40,217,.18)}
        
        /* Specific tab colors for rotational product */
        .rc-tab:nth-child(1).rc-tab-active{background:#2563eb !important;border-color:#2563eb !important;box-shadow:0 4px 14px rgba(37,99,235,.18) !important}
        .rc-tab:nth-child(2).rc-tab-active{background:#8b5cf6 !important;border-color:#8b5cf6 !important;box-shadow:0 4px 14px rgba(139,92,246,.18) !important}
        .rc-tab:nth-child(3).rc-tab-active{background:#06b6d4 !important;border-color:#06b6d4 !important;box-shadow:0 4px 14px rgba(6,182,212,.18) !important}
        .rc-tab:nth-child(4).rc-tab-active{background:#f59e0b !important;border-color:#f59e0b !important;box-shadow:0 4px 14px rgba(245,158,11,.18) !important}
        
        .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
        .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
        .uploadedimages{border-radius:10px}
          /* Thumbnail card so all X icons share the same anchor */
.img-card{
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  height: 180px;                 /* same height for all tiles */
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image fits nicely without changing card height */
.img-thumb{
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

/* Close button perfectly aligned inside the tile */
.img-x{
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 2;
  opacity: 0.9;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  padding: 0;
}

/* optional: hover feedback */
.img-card:hover .img-x{ opacity: 1; }
        @media (max-width:1021px){
          .rdt-desktop{display:none}
        }
        @media (min-width:1022px){
          .rdt-mobile{display:none}
        }
      `}</style>
    </div>
  );
};

export default SignageAddRotationalProduct;
